import { isValid, parse } from "date-fns";
import { enforce, test } from "vest";

import { DocumentValidatorProps } from "components/fax-form/fax-form-validator";

import { PATIENT_SOURCE } from "../components/fax-form/fax-form.component";

export const zipCodeIsValid = <T extends string>(
  name: T,
  label: string,
  fieldValue: any,
  country?: string
) => {
  const getErrorMessage = (country?: string) => {
    if (country === "Canada") {
      return `${label} is not valid. Example: M5V 3L9, K1A 0A6`;
    } else {
      return `${label} is not valid. Example: 12345, 12345-6789`;
    }
  };

  test(name, getErrorMessage(country), () => {
    const usZipRegex = /^\d{5}(-\d{4})?$/;
    const canadianPostalRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;

    enforce(fieldValue).condition((value) => {
      // Allow empty value: zip code is optional unless separately required
      if (
        value === undefined ||
        value === null ||
        String(value).trim() === ""
      ) {
        return { pass: true };
      }
      if (country === "Canada") {
        return {
          pass: canadianPostalRegex.test(value)
        };
      } else {
        // Default to US ZIP code format for all other countries
        return {
          pass: usZipRegex.test(value)
        };
      }
    });
  });
};

export const phoneIsValid = <T extends string>(
  name: T,
  label: string,
  fieldValue: any
) => {
  test(name, `${label} is not valid`, () => {
    enforce(fieldValue).matches(/^[[0-9]{3}?[-\s]?[0-9]{3}[-\s]?[0-9]{4,6}$/im);
  });
  test(name, `${label} must have 10 digits length`, () => {
    enforce(fieldValue).condition(
      (value) => value.replaceAll("-", "").trim().length === 10
    );
  });
};

//this email regexp was captured from https://emailregex.com/
export const EMAIL_REGEX =
  /^((([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})))?$/;

export const emailIsValid = <T extends string>(
  name: T,
  label: string,
  fieldValue: any
) => {
  test(name, `${label} is not valid`, () => {
    enforce(fieldValue).matches(EMAIL_REGEX);
  });
};

export const dateIsValid = <T extends string>(
  name: T,
  label: string,
  fieldValue: any,
  mask?: string
) => {
  test(name, `${label} is not valid`, () => {
    enforce(fieldValue).condition((value) => {
      if (typeof fieldValue === "string") {
        value = parse(fieldValue, mask ?? "MM/dd/yyyy", new Date());
      }
      return {
        pass: !isNaN(value) && value instanceof Date && isValid(value)
      };
    });
  });
};

export const roomIsAvailable = <T extends string>(
  name: T,
  label: string,
  fieldValue: any
) => {
  test(
    name,
    `${label} has a scheduling conflict. Please check 'Ignore Room Reservation' to proceed.`,
    () => {
      enforce(fieldValue).condition((value) => {
        return {
          pass: fieldValue
        };
      });
    }
  );
};

export const dateIsInFuture = <T extends string>(
  name: T,
  label: string,
  fieldValue: any,
  mask?: string
) => {
  test(name, `${label} should be in the future`, () => {
    enforce(fieldValue).condition((value) => {
      if (typeof fieldValue === "string") {
        value = parse(fieldValue, mask ?? "MM/dd/yyyy", new Date());
      }
      const isCorrect =
        !isNaN(value) && value instanceof Date && isValid(value);
      return {
        pass: isCorrect && value > new Date()
      };
    });
  });
};

export const nameIsValid = <T extends string>(
  name: T,
  label: string,
  fieldValue: any
) => {
  test(name, `${label} is not valid`, () => {
    enforce(fieldValue).matches(/^[a-z ,.'-]*$/i);
  });
};

export const required = <T extends string>(
  name: T,
  label: string,
  fieldValue: any,
  key: string = ""
) => {
  test(
    name,
    `${label} is required`,
    () => {
      enforce(fieldValue).isNotEmpty();
    },
    `${key}_${name}_${label}_${fieldValue ?? ""}`
  );
};

export const fileCount = <T extends string>(
  name: T,
  label: string,
  fieldValue: FileList | null
) => {
  test(name, `${label} count is more than 10`, () => {
    enforce(fieldValue).condition((values) => {
      return {
        pass: values && values.length <= 10
      };
    });
  });
};

export const nameIsRequired = <T extends string>(
  name: T,
  label: string,
  fieldValue: String | null
) => {
  test(name, `${label} is required when including cover letter`, () => {
    enforce(fieldValue).condition((values) => {
      return {
        pass: !!values
      };
    });
  });
};

export const documentIsRequired = <T extends string>(
  name: T,
  fieldValue: DocumentValidatorProps
) => {
  test(name, `Either include cover letter or upload a document`, () => {
    enforce(fieldValue).condition((values) => {
      return {
        pass:
          values.include_cover_page ||
          Boolean(values.documents?.length || values.source === PATIENT_SOURCE)
      };
    });
  });
};

export const MAX_FILE_SIZE = 10 * 1024 * 1024;

export const fileMax10Mb = <T extends string>(
  name: T,
  label: string,
  fieldValue: FileList | null
) => {
  test(name, `${label} size is more than 10 Mb`, () => {
    enforce(fieldValue).condition((values) => {
      return {
        pass:
          !values.length ||
          values.every((value: File) => value.size <= MAX_FILE_SIZE)
      };
    });
  });
};

export const requiredAndNameIsValid = <T extends string>(
  name: T,
  label: string,
  fieldValue: any
) => {
  required<T>(name, label, fieldValue, "requiredAndNameIsValid");
  nameIsValid<T>(name, label, fieldValue);
};

export const requiredIfDefined = <T extends string>(
  name: T,
  label: string,
  fieldValue: any
) => {
  test(name, `${label} is required`, () => {
    enforce(fieldValue).condition((value) => {
      if (value === undefined) {
        return { pass: true };
      }
      return {
        pass: value !== null && value !== "" && String(value).trim() !== ""
      };
    });
  });
};

export const minNumber = <T extends string>(
  name: T,
  label: string,
  fieldValue: any,
  min: number
) => {
  test(name, `${label} can't be less than ${min}`, () => {
    enforce(fieldValue).condition((value) => {
      value = Number(fieldValue);
      return {
        pass: !isNaN(value) && value >= min
      };
    });
  });
};

export const maxNumber = <T extends string>(
  name: T,
  label: string,
  fieldValue: any,
  min: number
) => {
  test(name, `${label} can't be greater than ${min}`, () => {
    enforce(fieldValue).condition((value) => {
      value = Number(fieldValue);
      return {
        pass: !isNaN(value) && value <= min
      };
    });
  });
};

export const maxLength = <T extends string>(
  name: T,
  label: string,
  fieldValue: string,
  max: number
) => {
  test(name, `${label} must be at most ${max} characters`, () => {
    return fieldValue.length <= max;
  });
};

export function getFileSizeError(currentSize: number, maxSize: number): string {
  if (currentSize <= maxSize) {
    return "";
  }
  const mb = maxSize / 1048576;
  const size = Number.isInteger(mb) ? mb : parseFloat(mb.toFixed(2));
  return `File can't be larger than ${size} MB`;
}
